import { makeAutoObservable } from 'mobx';
import { token } from '@/infrastructure/container';
import type { IAccessGateway } from '@/domain/accessGateway';
import { DomainError, ServerError } from '@/domain/errors';
import type { ScheduledSession } from '@/domain/project';
import type { IClock } from '@/services/Clock';
import type { IDomNavigation } from '@/services/DomNavigation';
import type { IDomStorage } from '@/services/DomStorage';
import type { IIdGen } from '@/services/IdGen';
import type { ILogger } from '@/services/Logger';
import type { TickEngine } from '@/services/TickEngine';
import type { MoveList, MoveRow, NumberingMode, SessionStatus } from '@/domain/session';
import type { MoveRow as PreviewRow } from '@/domain/rotation';
import type { AdminAccessStore } from './AdminAccessStore';

// Which grading day this laptop is running, and which session's codes are on the TV.
//
// The admin device is the project's CREATOR, so getting in is not a share redemption: there is
// nothing to redeem, because owning the aggregate is what minting requires and creating it is what
// confers ownership (docs/sync.md). It reaches its screen on `#/admin` and opens the project it
// made last time.
//
// The project id is therefore the one durable thing on this device, and it is kept in storage
// rather than in a URL because a laptop that reloads must find the SAME day rather than start a
// second one. No bearer key goes in there: keys are born here, live in memory, and reach the wall
// as pixels (docs/access.md). Lose the laptop and the codes are re-minted, not recovered.
//
// Kept apart from AdminAccessStore because that store's constructor is pinned by the phase contract
// and because the two answer different questions: this one is "which day and which session", that
// one is "schedule and mint".

const PROJECT_ID_KEY = 'grading-day.projectId';

export class AdminShellStore {
  private _projectId: string | undefined;
  private _selectedSessionId: string | undefined;
  private _opening = false;
  private _needsProject = false;
  private _error: string | undefined;
  // The clock display's own reading of the time. A getter that called `clock.nowMs()` would be a
  // non-observable read: the number would be right the moment React happened to render and stale
  // for the ten minutes after it. This is nudged once a second, which is the resolution "next tick
  // in 7 min" needs.
  private _nowMs: number;
  private stopClock: (() => void) | undefined;

  constructor(
    private readonly access: IAccessGateway,
    private readonly codes: AdminAccessStore,
    private readonly storage: IDomStorage,
    private readonly idGen: IIdGen,
    private readonly clock: IClock,
    private readonly nav: IDomNavigation,
    private readonly logger: ILogger,
    // Appended, so the seven-argument construction the P2 tests use still compiles. The engine is
    // the admin device's half of the tick: it stamps the start, it publishes the lists, and it is
    // where this screen reads the schedule from.
    private readonly engine?: TickEngine,
  ) {
    this._nowMs = clock.nowMs();
    makeAutoObservable(this);
  }

  // Set once the project handle is open. Until then the screen has nothing to show.
  get projectId(): string | undefined {
    return this._projectId;
  }

  get error(): string | undefined {
    return this._error;
  }

  // This device has no grading day stored, and will not invent one. The screen says so and offers
  // `startNewDay`. Distinct from `error`: nothing failed, there is simply nothing here yet.
  get needsProject(): boolean {
    return this._needsProject;
  }

  // The page's own origin, so the join URLs the TV renders point at the app the scanner is about to
  // load. A view reading `window.location` directly is INVARIANTS rule 12.
  get origin(): string {
    return this.nav.origin;
  }

  // The day's run list, newest scheduling last, straight off the project fold.
  get sessions(): readonly ScheduledSession[] {
    return this.access.projectState.sessions;
  }

  // Which session's six codes the TV is showing. The "fifteen minutes ahead" rule in
  // docs/access.md is admin behaviour, not code: the human selects, and the screen says which one
  // is up so nobody has to guess from the QR images.
  get selectedSessionId(): string | undefined {
    return this._selectedSessionId;
  }

  get selectedSession(): ScheduledSession | undefined {
    const id = this._selectedSessionId;
    return id === undefined ? undefined : this.sessions.find((s) => s.sessionId === id);
  }

  // --- the tick, as the admin watches it ------------------------------------------------------
  // All of it derived from the session fold this laptop has open, plus the clock reading above.
  // Nothing here is remembered from the press of Start: a laptop that opened the app at minute 35
  // shows the same numbers as one that has been running since the whistle.

  /**
   * Whether the engine's fold is the SELECTED session's. `selectSession` sets the id synchronously
   * and opens the aggregate asynchronously, so in between, and permanently if that open fails, the
   * engine is still holding the previous session's fold. Every reading below that names the
   * selected session has to pass through here or it reports another session's numbers under this
   * session's heading, which is a human turning a knob on the wrong day.
   *
   * Fails OPEN for a gateway that does not report an id, because that is every fake written before
   * the id existed and their folds are whatever the test seeded. The real `SessionGateway` always
   * reports, so in production this fails CLOSED.
   */
  private get engineOnSelected(): boolean {
    const open = this.engine?.openSessionId;
    return open === undefined || open === this._selectedSessionId;
  }

  /** The outlier constant in force, as the fold holds it. */
  get sigma(): number | undefined {
    return this.engineOnSelected ? this.engine?.sigma : undefined;
  }

  /** The rebalance tolerance in force. */
  get tolerance(): number | undefined {
    return this.engineOnSelected ? this.engine?.tolerance : undefined;
  }

  /**
   * Where this session's player numbers come from, or undefined with no session selected. Off the
   * fold through the engine, like the two knobs above, so it shows what the HALL is working under
   * and not what this laptop last clicked: another admin device flipping it is what this screen has
   * to reflect.
   */
  get numberingMode(): NumberingMode | undefined {
    if (this._selectedSessionId === undefined || !this.engineOnSelected) return undefined;
    return this.engine?.numbering;
  }

  /** Minutes since the ACTUAL start, or undefined before one. */
  get elapsedMinutes(): number | undefined {
    const elapsed = this.engine?.elapsedMs(this._nowMs);
    return elapsed === undefined ? undefined : Math.floor(elapsed / 60_000);
  }

  /** Which tick the hall is waiting for, 1..6, or undefined once the last one has landed. */
  get nextTick(): number | undefined {
    return this.engine?.nextTick(this._nowMs);
  }

  /** Minutes until it is due, rounded up so "0 min" means it is landing rather than overdue. */
  get nextTickInMinutes(): number | undefined {
    const due = this.engine?.nextTickInMs(this._nowMs);
    return due === undefined ? undefined : Math.ceil(due / 60_000);
  }

  /** The list the movers are walking. The admin watches the tick land on this. */
  get moveList(): MoveList | undefined {
    return this.engine?.moveList;
  }

  /** The rows of that list not yet actioned, by the mover screen's own rule (TickEngine). */
  get outstandingMoves(): readonly MoveRow[] {
    return this.engine?.outstandingMoves ?? [];
  }

  /**
   * The SELECTED session's status off its fold, or undefined with no selection. Only the selected
   * session's aggregate is open on this device (one handle per aggregate), so the run list cannot
   * gate the other rows — their Start still relies on the aggregate's own precondition refusing a
   * double start.
   */
  get selectedStatus(): SessionStatus | undefined {
    if (this._selectedSessionId === undefined || !this.engineOnSelected) return undefined;
    return this.engine?.status;
  }

  /**
   * The forecast: what the next tick would move on the fold as it stands, reasons included, so
   * the admin can turn the knobs and watch this settle BEFORE the wake makes it real.
   */
  get pendingMoves(): readonly PreviewRow[] {
    if (this._selectedSessionId === undefined) return [];
    return this.engine?.previewMoves ?? [];
  }

  // Idempotent: the shell calls it every time the URL lands on `#/admin`.
  //
  // On a storage MISS this mints nothing. A missing project id means a new browser profile, a
  // cleared cache, a second laptop, or any phone that typed `#/admin`, and minting one there is
  // silently destructive: the fresh id 404s to an empty projection, so the open SUCCEEDS and the
  // screen shows a blank run list under a project nobody has ever seen, while the day the admin
  // actually scheduled is unreachable from the UI with nothing on screen to say so. The device
  // says it has no day and offers to start one, and a human decides.
  init = async (): Promise<void> => {
    // A device that already has its day open is back on the admin route after standing down, so
    // re-arm BOTH timers. `startClock` is idempotent, so the ordinary re-entry costs nothing.
    //
    // The tick pulse has to come back with the display clock, and it is the half that used to be
    // missed: `standDown` disposes the engine, so a laptop that scanned its own QR off the TV and
    // came back had a moving countdown over a hall with ZERO calculators, and nothing on the screen
    // said so. Guarded on a selection because the pulse belongs to a session — a laptop that has
    // opened its day but picked no row has nothing to tick — and armed through the engine's own
    // idempotent `startTicking()` rather than `openSelected`, because the handle is already open
    // and re-opening it would put a network call on the back button.
    if (this._projectId !== undefined) {
      this.startClock();
      if (this._selectedSessionId !== undefined) this.engine?.startTicking();
      return;
    }
    if (this._opening) return;
    const stored = this.storage.get(PROJECT_ID_KEY);
    if (stored === undefined) {
      this.needProject();
      return;
    }
    await this.open(stored);
  };

  // Stop computing. The shell calls this whenever the URL is NOT the admin route, which is the
  // owner the P6 scraps item asked for: `TickEngine.dispose()` and this store's own display ticker
  // both existed, both were tested, and nothing in the app invoked either.
  //
  // The case that is not benign is the admin laptop redeeming its own QR off the TV and landing on
  // a coach or mover screen with the tick pulse still armed. That device goes on computing move
  // lists for a session it is no longer watching — a second calculator, which is the thing
  // docs/rotation.md's "one calculator, many actioners" exists to prevent. Both timers are
  // idempotent to stop, and `init` re-arms both of them if the admin comes back.
  standDown = (): void => {
    this.engine?.dispose();
    this.stopClock?.();
    this.stopClock = undefined;
  };

  // The explicit "yes, start a new grading day" the init path refuses to take on its own. Nothing
  // else mints a project id.
  startNewDay = async (): Promise<void> => {
    if (this._projectId !== undefined || this._opening) return;
    await this.open(this.idGen.next());
  };

  private open = async (projectId: string): Promise<void> => {
    // Written BEFORE the open, not after: an open that fails must not cost the day a fresh project
    // id on the next attempt, or a flaky wifi turns one grading day into three.
    this.storage.set(PROJECT_ID_KEY, projectId);
    this.startOpening();
    try {
      // A read of a project nobody has written yet 404s, which the SDK folds to an empty
      // projection. The aggregate is created by the first `sessionScheduled` write, and that write
      // is what makes this device its owner (docs/sync.md).
      await this.access.openProject(projectId);
      this.opened(projectId);
      this.startClock();
      // The other half of the reload story: re-select what was selected, which re-opens the
      // session and re-arms the pulse through `openSelected` — the arming site. Only when the run
      // list still knows the id, so a stale key from a wiped day selects nothing.
      const remembered = this.storage.get(`grading-day.selected.${projectId}`);
      if (
        this._selectedSessionId === undefined &&
        remembered !== undefined &&
        this.sessions.some((s) => s.sessionId === remembered)
      ) {
        this.selectSession(remembered);
      }
    } catch (cause) {
      const failure =
        cause instanceof DomainError
          ? cause
          : new ServerError('Could not open the project.', { cause });
      this.logger.error('admin: opening the project failed', failure.tag);
      this.failed(failure.message);
    } finally {
      this.stopOpening();
    }
  };

  // Picking a session on the run list also OPENS it, which is what puts the tick clock, the two
  // knobs and the current move list on this screen. Fire-and-forget on purpose: the selection is a
  // view change that must not wait on the network, and `openSelected` surfaces its own failure.
  selectSession = (sessionId: string): void => {
    this._selectedSessionId = sessionId;
    // Remembered per project, because a reload used to come back with NOTHING selected: no session
    // gateway open, no tick pulse armed, a running hall with zero calculators and a screen that
    // did not say so. Keyed by project so a new day never inherits last week's selection.
    if (this._projectId !== undefined) {
      this.storage.set(`grading-day.selected.${this._projectId}`, sessionId);
    }
    void this.openSelected(sessionId);
  };

  // The four buttons on the admin screen. Each of them is `void store.method()` in the view, so
  // none of them may reject: a rejection out of a `void` call reaches no catch, no caller and no
  // error boundary (INVARIANTS rule 17). AdminAccessStore rejects on purpose — its contract says so
  // and its oracle asserts it — and this is the layer that absorbs that, because the message is
  // already on `AdminAccessStore.lastError` and the screen is already rendering it.
  //
  // Naming a session properly is a typing job the laptop has no keyboard affordance for yet, so
  // the sessions are numbered and the time is now; docs/session-lifecycle.md wants "under 19 boys,
  // 11am" and getting there is a text field, marked in visual-residual.md. The clock is injected
  // because a store may not read `Date.now()` (INVARIANTS rule 6) and because `startsAtMs` is a
  // payload a reducer sees.
  scheduleNextSession = async (): Promise<void> => {
    await this.attempt(() =>
      this.codes.scheduleSession(`session ${this.sessions.length + 1}`, this.clock.nowMs()),
    );
  };

  mintCodes = async (sessionId: string): Promise<void> => {
    await this.attempt(() => this.codes.mintCodes(sessionId));
    this.selectSession(sessionId);
  };

  // Start goes through the ENGINE, not through the access store: the event has to carry this
  // device's clock so every tick is measured from the actual start (docs/session-lifecycle.md), and
  // the object that owns the schedule is the one that should be stamping it. The session handle is
  // opened first, because the engine writes and reads through it.
  //
  // The selection moves first. The hour panel and the two knobs read `selectedSessionId`, so
  // starting a session the run list has not selected would put the clock, the sigma knob and the
  // tolerance knob on a different session from the one that just started.
  //
  // The engine reports whether the start landed rather than throwing, so a start that never
  // reached the server says so on the screen instead of leaving a Start button that looks pressed.
  startSession = async (sessionId: string): Promise<void> => {
    this.selectFor(sessionId);
    await this.attempt(async () => {
      await this.openSelected(sessionId);
      // `=== false` rather than a falsy test: a laptop with no engine wired answers undefined, and
      // that is a build without the hour panel, not a start that failed.
      if ((await this.engine?.start()) === false) {
        this.failed('The session did not start. Check the connection and try again.');
      }
    });
  };

  stopSession = async (sessionId: string): Promise<void> => {
    await this.attempt(() => this.codes.stopSession(sessionId));
  };

  // The two knobs. The admin turns sigma down when nobody is moving (docs/user-journeys.md,
  // "Admin"), and the tolerance when the courts are drifting apart.
  setSigma = async (sigma: number): Promise<void> => {
    const sessionId = this._selectedSessionId;
    if (sessionId === undefined) return;
    await this.attempt(() => this.codes.setSigma(sessionId, sigma));
  };

  // The numbering mode, same shape as the two knobs. Never rejects: the view calls it as
  // `void store.setNumberingMode(...)` and a rejection out of a `void` call reaches no catch, no
  // caller and no error boundary (INVARIANTS rule 17).
  setNumberingMode = async (mode: NumberingMode): Promise<void> => {
    const sessionId = this._selectedSessionId;
    if (sessionId === undefined) return;
    await this.attempt(() => this.codes.setNumberingMode(sessionId, mode));
  };

  setTolerance = async (tolerance: number): Promise<void> => {
    const sessionId = this._selectedSessionId;
    if (sessionId === undefined) return;
    await this.attempt(() => this.codes.setTolerance(sessionId, tolerance));
  };

  // Opens the session aggregate on this laptop and arms the tick pulse. Idempotent for a session
  // already open (SessionGateway short-circuits), and the ONLY place the pulse is armed: every
  // device in the building resolves the same graph, and a coach's phone computing move lists would
  // be a second calculator (docs/rotation.md, "One calculator, many actioners").
  private openSelected = async (sessionId: string): Promise<void> => {
    try {
      await this.access.openSession(sessionId);
      this.engine?.startTicking();
    } catch (cause) {
      const failure =
        cause instanceof DomainError
          ? cause
          : new ServerError('Could not open the session.', { cause });
      this.logger.warn('admin: opening the session failed', failure.tag);
      // On the screen, not only in the log. An open that fails leaves this laptop holding the
      // PREVIOUS session's fold with the new session's row selected, so every reading gated on
      // `engineOnSelected` goes blank and the panels vanish. A blank panel with nothing said reads
      // as "this session has no settings"; it means "this laptop could not read this session".
      this.failed(failure.message);
    }
  };

  private attempt = async (run: () => Promise<void>): Promise<void> => {
    try {
      await run();
    } catch (cause) {
      const failure =
        cause instanceof DomainError ? cause : new ServerError('That did not land.', { cause });
      this.logger.warn('admin: action abandoned', failure.tag);
    }
  };

  // One second, and only on a laptop that actually opened a grading day. It exists so the elapsed
  // and next-tick readings move on their own; nothing about the tick SCHEDULE depends on it, which
  // is why the engine's own pulse is a separate, slower one.
  private startClock(): void {
    if (this.stopClock !== undefined) return;
    this.stopClock = this.clock.startTicker(1_000, () => this.readClock());
  }

  private readClock(): void {
    this._nowMs = this.clock.nowMs();
  }

  private needProject(): void {
    this._needsProject = true;
  }

  private startOpening(): void {
    this._opening = true;
    this._needsProject = false;
    this._error = undefined;
  }

  private stopOpening(): void {
    this._opening = false;
  }

  private opened(projectId: string): void {
    this._projectId = projectId;
  }

  private failed(message: string): void {
    this._error = message;
  }

  // Selecting a session is also what clears the last failure off the screen: the message belongs to
  // an attempt on a session, and carrying it onto the next one reads as a fresh failure.
  private selectFor(sessionId: string): void {
    this._selectedSessionId = sessionId;
    this._error = undefined;
  }
}

export const TOKEN_AdminShellStore = token<AdminShellStore>('AdminShellStore');
